import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Clock, X, AlertCircle, CheckCircle, Loader2, Calendar, Mail, Trash2, RefreshCw, ChevronRight } from 'lucide-react'
import { getScheduledEmails, getScheduledEmailsStats, cancelScheduledEmail } from '../../services/api'
import toast from 'react-hot-toast'

export default function ScheduledEmails() {
  const queryClient = useQueryClient()
  const [statusFilter, setStatusFilter] = useState('')
  const [expandedEmails, setExpandedEmails] = useState(new Set())

  const { data: emailsData, isLoading: emailsLoading, refetch } = useQuery({
    queryKey: ['scheduledEmails', statusFilter],
    queryFn: () => getScheduledEmails(statusFilter || null)
  })

  const { data: statsData, refetch: refetchStats } = useQuery({
    queryKey: ['scheduledEmailsStats'],
    queryFn: getScheduledEmailsStats
  })

  const cancelMutation = useMutation({
    mutationFn: cancelScheduledEmail,
    onSuccess: () => {
      refetch()
      refetchStats()
      toast.success('Email annulé')
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Erreur lors de l\'annulation')
    }
  })

  const emails = emailsData?.emails || []
  const stats = statsData?.stats || { pending: 0, sent: 0, failed: 0, cancelled: 0 }

  const handleRefresh = () => {
    refetch()
    refetchStats()
    toast.success('Actualisé')
  }

  const toggleExpand = (id) => {
    const newExpanded = new Set(expandedEmails)
    if (newExpanded.has(id)) {
      newExpanded.delete(id)
    } else {
      newExpanded.add(id)
    }
    setExpandedEmails(newExpanded)
  }

  const formatDate = (dateStr) => {
    const date = new Date(dateStr)
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = String(date.getFullYear()).slice(-2)
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    const seconds = String(date.getSeconds()).padStart(2, '0')
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`
  }

  const getStatusInfo = (status) => {
    const map = {
      pending: { label: 'En attente', icon: Clock },
      processing: { label: 'En cours', icon: Loader2 },
      sent: { label: 'Envoyé', icon: CheckCircle },
      failed: { label: 'Échec', icon: AlertCircle },
      cancelled: { label: 'Annulé', icon: X }
    }
    return map[status] || map.pending
  }

  if (emailsLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-secondary)' }}>
        <Loader2 size={32} className="spinning" style={{ marginBottom: '1rem' }} />
        <div>Chargement...</div>
      </div>
    )
  }

  return (
    <div>
      {/* Stats - Style identique à l'historique */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        {[
          { label: 'En attente', value: stats.pending },
          { label: 'Envoyés', value: stats.sent },
          { label: 'Échecs', value: stats.failed },
          { label: 'Annulés', value: stats.cancelled }
        ].map(stat => (
          <div key={stat.label} style={{
            background: 'rgb(var(--rgb-ink) / 0.02)',
            border: '1px solid rgb(var(--rgb-ink) / 0.1)',
            borderRadius: '12px',
            padding: '1.5rem'
          }}>
            <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
              {stat.label}
            </div>
            <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--color-text)' }}>
              {stat.value}
            </div>
          </div>
        ))}
      </div>

      {/* Filtres et Actualiser */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {[
            { value: '', label: 'Tous' },
            { value: 'pending', label: 'En attente' },
            { value: 'sent', label: 'Envoyés' },
            { value: 'failed', label: 'Échecs' }
          ].map(filter => (
            <button
              key={filter.value}
              onClick={() => setStatusFilter(filter.value)}
              style={{
                padding: '0.5rem 1rem',
                background: statusFilter === filter.value ? 'rgb(var(--rgb-ink) / 0.1)' : 'transparent',
                border: '1px solid',
                borderColor: statusFilter === filter.value ? 'rgb(var(--rgb-ink) / 0.2)' : 'rgb(var(--rgb-ink) / 0.1)',
                borderRadius: '4px',
                color: statusFilter === filter.value ? 'rgb(var(--rgb-ink))' : 'var(--color-text-secondary)',
                fontSize: '0.875rem',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              {filter.label}
            </button>
          ))}
        </div>
        <button
          onClick={handleRefresh}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem 1rem',
            background: 'transparent',
            border: '1px solid rgb(var(--rgb-ink) / 0.1)',
            borderRadius: '4px',
            color: 'var(--color-text-secondary)',
            fontSize: '0.875rem',
            cursor: 'pointer'
          }}
        >
          <RefreshCw size={14} />
          Actualiser
        </button>
      </div>

      {/* Liste */}
      <div style={{
        background: 'rgb(var(--rgb-ink) / 0.02)',
        border: '1px solid rgb(var(--rgb-ink) / 0.1)',
        borderRadius: '12px',
        overflow: 'hidden'
      }}>
        {emails.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '4rem 2rem',
            color: 'var(--color-text-muted)'
          }}>
            <Calendar size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
            <h3 style={{ margin: '0 0 0.5rem', color: 'var(--color-text-secondary)', fontWeight: 500 }}>Aucun email planifié</h3>
            <p style={{ margin: 0, fontSize: '0.875rem' }}>Les emails que vous planifierez apparaîtront ici.</p>
          </div>
        ) : (
          emails.map((email, index) => {
            const StatusIcon = getStatusInfo(email.status).icon
            const isExpanded = expandedEmails.has(email.id)
            
            return (
              <div
                key={email.id}
                style={{
                  borderBottom: index < emails.length - 1 ? '1px solid rgb(var(--rgb-ink) / 0.05)' : 'none'
                }}
              >
                {/* Header row */}
                <div
                  onClick={() => toggleExpand(email.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '1rem 1.5rem',
                    cursor: 'pointer',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgb(var(--rgb-ink) / 0.02)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <ChevronRight
                    size={16}
                    style={{
                      marginRight: '0.75rem',
                      color: 'var(--color-text-disabled)',
                      transform: isExpanded ? 'rotate(90deg)' : 'none',
                      transition: 'transform 0.2s'
                    }}
                  />
                  <StatusIcon
                    size={16}
                    style={{ marginRight: '0.75rem', color: 'rgb(var(--rgb-brand))' }}
                    className={email.status === 'processing' ? 'spinning' : ''}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <span style={{ color: 'rgb(var(--rgb-brand))', fontWeight: 500 }}>{email.recipientEmail}</span>
                    </div>
                    <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                      {email.subject}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', marginLeft: '1rem' }}>
                    <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.75rem' }}>
                      {formatDate(email.scheduledAt)}
                    </div>
                    <div style={{
                      fontSize: '0.75rem',
                      color: email.status === 'sent' ? 'rgb(var(--rgb-brand))' : 
                             email.status === 'failed' ? '#ef4444' : 'var(--color-text-secondary)',
                      marginTop: '0.25rem'
                    }}>
                      {getStatusInfo(email.status).label}
                    </div>
                  </div>
                </div>

                {/* Expanded content */}
                {isExpanded && (
                  <div style={{
                    padding: '0 1.5rem 1rem 3rem',
                    background: 'rgb(var(--rgb-shade) / 0.2)'
                  }}>
                    <div style={{
                      padding: '1rem',
                      background: 'rgb(var(--rgb-ink) / 0.02)',
                      borderRadius: '4px',
                      fontSize: '0.875rem',
                      color: 'var(--color-text-secondary)',
                      whiteSpace: 'pre-wrap',
                      maxHeight: '200px',
                      overflow: 'auto'
                    }}>
                      {email.body || '(Pas de contenu)'}
                    </div>
                    
                    {email.status === 'failed' && email.errorMessage && (
                      <div style={{
                        marginTop: '0.75rem',
                        padding: '0.75rem',
                        background: 'rgba(239, 68, 68, 0.1)',
                        border: '1px solid rgba(239, 68, 68, 0.2)',
                        borderRadius: '4px',
                        fontSize: '0.8rem',
                        color: '#ef4444'
                      }}>
                        Erreur: {email.errorMessage}
                      </div>
                    )}
                    
                    {email.status === 'pending' && (
                      <div style={{ marginTop: '1rem' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            if (window.confirm('Annuler cet email planifié ?')) {
                              cancelMutation.mutate(email.id)
                            }
                          }}
                          disabled={cancelMutation.isPending}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.5rem 1rem',
                            background: 'rgba(239, 68, 68, 0.1)',
                            border: '1px solid rgba(239, 68, 68, 0.3)',
                            borderRadius: '4px',
                            color: '#ef4444',
                            fontSize: '0.8rem',
                            cursor: 'pointer'
                          }}
                        >
                          <Trash2 size={14} />
                          Annuler cet email
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}
